import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, Upload } from 'lucide-react';

interface SiteLogo {
  id: string;
  name: string;
  image_url: string;
  alt_text: string;
  is_active: boolean;
  navbar_height_mobile?: number;
  navbar_height_desktop?: number;
  footer_height?: number;
}

interface SiteLogoManagementDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  logo: SiteLogo | null;
}

interface FormData {
  name: string;
  alt_text: string;
  navbar_height_mobile: number;
  navbar_height_desktop: number;
  footer_height: number;
}


export function SiteLogoManagementDialog({
  open,
  onOpenChange,
  logo,
}: SiteLogoManagementDialogProps) {
  const queryClient = useQueryClient();
  const [uploading, setUploading] = useState(false);
  const [uploadedUrl, setUploadedUrl] = useState<string | null>(null);

  const { register, handleSubmit, reset, formState: { errors } } = useForm<FormData>({
    defaultValues: {
      name: '',
      alt_text: '4Fox Cyber Security Logo',
      navbar_height_mobile: 40,
      navbar_height_desktop: 56,
      footer_height: 56,
    },
  });

  useEffect(() => {
    if (logo) {
      reset({
        name: logo.name,
        alt_text: logo.alt_text,
        navbar_height_mobile: logo.navbar_height_mobile ?? 40,
        navbar_height_desktop: logo.navbar_height_desktop ?? 56,
        footer_height: logo.footer_height ?? 56,
      });
      setUploadedUrl(logo.image_url);
    } else {
      reset({
        name: '',
        alt_text: '4Fox Cyber Security Logo',
        navbar_height_mobile: 40,
        navbar_height_desktop: 56,
        footer_height: 56,
      });
      setUploadedUrl(null);
    }
  }, [logo, reset]);

  const saveMutation = useMutation({
    mutationFn: async (data: FormData & { image_url: string }) => {
      if (logo) {
        const { error } = await supabase
          .from('site_logos')
          .update({
            name: data.name,
            image_url: data.image_url,
            alt_text: data.alt_text,
            navbar_height_mobile: data.navbar_height_mobile,
            navbar_height_desktop: data.navbar_height_desktop,
            footer_height: data.footer_height,
          })
          .eq('id', logo.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('site_logos')
          .insert({
            name: data.name,
            image_url: data.image_url,
            alt_text: data.alt_text,
            is_active: false,
            navbar_height_mobile: data.navbar_height_mobile,
            navbar_height_desktop: data.navbar_height_desktop,
            footer_height: data.footer_height,
          });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-site-logos'] });
      queryClient.invalidateQueries({ queryKey: ['active-site-logo'] });
      toast.success(logo ? 'Logo updated successfully' : 'Logo added successfully');
      onOpenChange(false);
    },
    onError: (error) => {
      toast.error(`Failed to save logo: ${error.message}`);
    },
  });

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    setUploading(true);
    try {
      // Upload original file to preserve embedded color profiles (canvas resizing can shift dark blues to black)
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const fileName = `${Date.now()}-${safeName}`;

      const { error: uploadError } = await supabase.storage
        .from('site-logos')
        .upload(fileName, file, {
          contentType: file.type,
        });

      if (uploadError) throw uploadError;

      const {
        data: { publicUrl },
      } = supabase.storage.from('site-logos').getPublicUrl(fileName);

      setUploadedUrl(publicUrl);
      toast.success('Image uploaded successfully');
    } catch (error: any) {
      toast.error(`Upload failed: ${error.message}`);
    } finally {
      setUploading(false);
    }
  };

  const onSubmit = (data: FormData) => {
    if (!uploadedUrl) {
      toast.error('Please upload an image');
      return;
    }
    saveMutation.mutate({ ...data, image_url: uploadedUrl });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{logo ? 'Edit Site Logo' : 'Add Site Logo'}</DialogTitle>
          <DialogDescription>
            {logo ? 'Update the site logo details.' : 'Upload a new site logo (e.g., seasonal themes).'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Name *</Label>
            <Input
              id="name"
              placeholder="e.g., Christmas Logo"
              {...register('name', { required: 'Name is required' })}
            />
            {errors.name && (
              <p className="text-sm text-destructive">{errors.name.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="alt_text">Alt Text *</Label>
            <Input
              id="alt_text"
              placeholder="Logo alt text for accessibility"
              {...register('alt_text', { required: 'Alt text is required' })}
            />
            {errors.alt_text && (
              <p className="text-sm text-destructive">{errors.alt_text.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label>Logo Image *</Label>
            <div className="flex items-center gap-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => document.getElementById('logo-upload')?.click()}
                disabled={uploading}
              >
                {uploading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="mr-2 h-4 w-4" />
                    Upload Image
                  </>
                )}
              </Button>
              <input
                id="logo-upload"
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleFileUpload}
              />
            </div>
            {uploadedUrl && (
              <div className="mt-2 p-4 bg-muted rounded-lg">
                <img
                  src={uploadedUrl}
                  alt="Preview"
                  className="h-16 object-contain"
                />
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-medium">Display Sizes (pixels)</Label>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label htmlFor="navbar_height_mobile" className="text-xs text-muted-foreground">
                  Navbar (Mobile)
                </Label>
                <Input
                  id="navbar_height_mobile"
                  type="number"
                  min={20}
                  max={100}
                  {...register('navbar_height_mobile', { 
                    required: true, 
                    valueAsNumber: true,
                    min: 20,
                    max: 100
                  })}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="navbar_height_desktop" className="text-xs text-muted-foreground">
                  Navbar (Desktop)
                </Label>
                <Input
                  id="navbar_height_desktop"
                  type="number"
                  min={20}
                  max={120}
                  {...register('navbar_height_desktop', { 
                    required: true, 
                    valueAsNumber: true,
                    min: 20,
                    max: 120
                  })}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="footer_height" className="text-xs text-muted-foreground">
                  Footer
                </Label>
                <Input
                  id="footer_height"
                  type="number"
                  min={20}
                  max={120}
                  {...register('footer_height', { 
                    required: true, 
                    valueAsNumber: true,
                    min: 20,
                    max: 120
                  })}
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={saveMutation.isPending || uploading}>
              {saveMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {logo ? 'Update' : 'Add'} Logo
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
